import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mget, mpost, mpatch, mdel, errMsg, eur } from "@/manager/lib";
import { Card, Field, Pill, goldBtn } from "@/manager/ui";

const ASG = { asignado: "bg-amber-100 text-amber-800", confirmado: "bg-emerald-100 text-emerald-800", rechazado: "bg-rose-100 text-rose-700" };

export default function StaffTab({ ev, save, reload }) {
  const [staff, setStaff] = useState([]);
  const [pick, setPick] = useState("");
  const [needed, setNeeded] = useState(ev.staff_needed ?? "");
  useEffect(() => { mget("/staff").then((s) => setStaff(s.filter((x) => x.active).sort((a, b) => (/camarer/i.test(b.role) ? 1 : 0) - (/camarer/i.test(a.role) ? 1 : 0)))); }, []);
  useEffect(() => { setNeeded(ev.staff_needed ?? ""); }, [ev.staff_needed]);
  const st = ev.staff;
  const assigned = new Set(ev.assignments.map((a) => a.staff_id));

  const add = async () => {
    if (!pick) return;
    try { const a = await mpost("/assignments", { event_id: ev.id, staff_id: pick }); if (a.warning) toast.warning(a.warning); setPick(""); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const setStatus = async (a, status) => { await mpatch(`/assignments/${a.id}`, { status }); reload(); };
  const remove = async (a) => { await mdel(`/assignments/${a.id}`); reload(); };

  return (
    <div className="space-y-5">
      <Card className="p-5 grid grid-cols-2 md:grid-cols-5 gap-4 items-end">
        <div><p className="text-[11px] uppercase tracking-[0.14em] text-[#6b6a66]">Recomendado</p><p className="font-display text-3xl" data-testid="staff-recommended">{st.recommended}</p><p className="text-[11px] text-[#6b6a66]">según {ev.guests ?? "?"} invitados</p></div>
        <div><p className="text-[11px] uppercase tracking-[0.14em] text-[#6b6a66]">Necesario</p><p className="font-display text-3xl" data-testid="staff-needed">{st.needed}</p></div>
        <div><p className="text-[11px] uppercase tracking-[0.14em] text-[#6b6a66]">Asignados</p><p className="font-display text-3xl">{st.assigned}</p></div>
        <div><p className="text-[11px] uppercase tracking-[0.14em] text-[#6b6a66]">Confirmados</p><p className={`font-display text-3xl ${st.missing ? "text-rose-600" : "text-emerald-700"}`} data-testid="staff-confirmed">{st.confirmed}</p></div>
        <Field label="Cambiar necesarios">
          <div className="flex gap-2">
            <Input type="number" min="0" value={needed} placeholder={String(st.recommended)} onChange={(e) => setNeeded(e.target.value)} data-testid="staff-needed-input" />
            <Button variant="outline" onClick={() => save({ staff_needed: needed === "" ? null : Number(needed) }, "Personal necesario actualizado")} data-testid="staff-needed-save">OK</Button>
          </div>
        </Field>
      </Card>
      {st.missing > 0 && <p className="text-sm text-rose-700 flex items-center gap-2" data-testid="staff-missing-alert"><AlertTriangle size={15} /> Faltan {st.missing} persona(s) por confirmar. Una plaza solo cuenta como cubierta cuando está confirmada.</p>}
      <Card className="p-5">
        <div className="flex flex-wrap gap-2 mb-4">
          <Select value={pick} onValueChange={setPick}>
            <SelectTrigger className="w-64" data-testid="staff-pick"><SelectValue placeholder="Elegir persona…" /></SelectTrigger>
            <SelectContent>{staff.filter((s) => !assigned.has(s.id)).map((s) => <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}</SelectItem>)}</SelectContent>
          </Select>
          <Button className={goldBtn} onClick={add} data-testid="staff-assign">Asignar</Button>
        </div>
        {ev.assignments.map((a) => (
          <div key={a.id} className="flex flex-wrap items-center gap-3 py-2 border-t border-[#f0ece4]" data-testid={`asg-${a.id}`}>
            <span className="flex-1 text-sm font-medium">{a.staff_name}</span>
            <span className="text-xs text-[#6b6a66]">{a.start_time || "--"}–{a.end_time || "--"} · {eur(a.cost)}</span>
            {a.warning && <Pill className="bg-rose-50 text-rose-700">Horario solapado</Pill>}
            <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
              <SelectTrigger className={`w-36 h-8 text-xs ${ASG[a.status]}`} data-testid={`asg-status-${a.id}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="asignado">Asignado</SelectItem><SelectItem value="confirmado">Confirmado</SelectItem><SelectItem value="rechazado">Rechazado</SelectItem></SelectContent>
            </Select>
            <button onClick={() => remove(a)} className="text-rose-700" data-testid={`asg-remove-${a.id}`}><Trash2 size={15} /></button>
          </div>
        ))}
        {!ev.assignments.length && <p className="text-sm text-[#6b6a66]">Nadie asignado todavía. Da de alta al personal en «Personal».</p>}
      </Card>
    </div>
  );
}
